import { z } from 'zod'

import { nodePropertyNameSchema } from './common.ts'
import { viewConfigFieldSchema } from './field.ts'
import { viewConfigFilterNodeSchema } from './filter.ts'
import { viewConfigTreeNodeSchema } from './tree.ts'

export const viewConfigSorterSchema = z.object({
  type: z.literal(`property`), // customも許してunionにする想定
  property: nodePropertyNameSchema,
  order: z.enum(['asc', 'desc']),
  // optional なのは、sortBy 追加前に保存されたレガシー config にキーが存在しないため。
  // 未指定は保持値ソート（compile 境界で 'value' に正規化）。
  //
  // 既知リスク（受容済み）: フロントは CDN 配信のためデプロイ後も旧バンドルが一定期間共存する。
  // 旧バンドルの並び替え保存処理は sorter を既知キー（type / property / order）のみで組み立て直すため、
  // 新バンドルが保存した sortBy は旧バンドルでの再保存時に silent に脱落し、そのビューは保持値ソートに戻る。
  // 発生は共存期間中 × 同一ビューを旧バンドル利用者が再保存した場合のみで、失われるのは並び設定 1 項目
  // （再設定で回復可能・エラーやデータ破壊なし）のため受容する。未知キーを spread で保持する恒久策は別対応。
  sortBy: z.enum(['value', 'label']).optional(),
})
export type ViewConfigSorter = z.infer<typeof viewConfigSorterSchema>

export const viewConfigMetaSchema = z.object({
  fixedLeftColumnIndex: z.number().optional(),
  visibleRowNum: z.number().optional(),
  searchQuery: z.string().optional(),
  isGoalView: z.boolean().optional(), // 目標設定で使う際は、特別な表示をする。こんな設定をいれたくないが、仕方なさそう
  shouldJoinRecordsInApplication: z.boolean().optional(), // アプリケーションジョインを使うかどうか。デフォルトはfalse。
})
export type ViewConfigMeta = z.infer<typeof viewConfigMetaSchema>

export const sheetPresetSchema = z.object({
  name: z.string(), // unique
  tree: viewConfigTreeNodeSchema.optional(),
  fields: viewConfigFieldSchema.array().optional(),
  filterTree: viewConfigFilterNodeSchema.optional(),
  sorters: viewConfigSorterSchema.array().optional(),
  meta: viewConfigMetaSchema.optional(),
})
export type ViewConfigSheetPreset = z.infer<typeof sheetPresetSchema>

export const viewConfigSheetExtraPeriodSchema = z.object({
  startAt: z.string().optional(),
  endAt: z.string().optional(),
  name: z.string(),
  nameLabel: z.string().optional(),
  groupName: z.string().optional(),
  groupNameLabel: z.string().optional(),
})
export type ViewConfigSheetExtraPeriod = z.infer<typeof viewConfigSheetExtraPeriodSchema>

const viewConfigSheetExtraSchema = z.object({
  // 現状目標入力画面でのみ使用。期間設定
  period: viewConfigSheetExtraPeriodSchema.optional(),
})

//
// config
//
export const ViewConfigSheetSchema = z.object({
  type: z.literal(`sheet`),
  tree: viewConfigTreeNodeSchema.optional(), // 未設定のとき、プロパティは存在しない。設定済みの時は必須
  fields: viewConfigFieldSchema.array().optional(), // 同上
  filterTree: viewConfigFilterNodeSchema.optional(),
  sorters: viewConfigSorterSchema.array().optional(),
  presets: sheetPresetSchema.array().optional(),
  meta: viewConfigMetaSchema.optional(),
  extra: viewConfigSheetExtraSchema.optional(),
})
export type ViewConfigSheet = z.infer<typeof ViewConfigSheetSchema>
